import React from "react";
import { graphql } from "react-relay";
import { Link, useParams } from "react-router-dom";
import { AutomationTaskPageQuery } from "./__generated__/AutomationTaskPageQuery.graphql";
import { useLazyLoadQuery } from "react-relay/hooks";
import { Accordion, AccordionDetails, AccordionSummary, Container, Typography } from "@mui/material";
import KeyValueTable from "../../components/common/KeyValueTable";
import InfoTable from "../../components/common/InfoTable";
import FileTable from "./FileTable";
import SimpleBackdrop from "../../components/common/SimpleBackdrop";
import HazardTable from "./HazardTable";
import ConfigTable from "./ConfigTable";
import PageContainer from "../../components/common/PageContainer";
import { PrettyJson } from "../../components/common/PrettyJson";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type AutomationTaskPageParams = {
  id: string;
};

const AutomationTask: React.FC = () => {
  const { id } = useParams<AutomationTaskPageParams>();
  const data = useLazyLoadQuery<AutomationTaskPageQuery>(automationTaskPageQuery, { id: id || "" });

  if (!data?.node) {
    return (
      <Typography variant="h5" gutterBottom>
        Task: Id Not Found
      </Typography>
    );
  }

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="h5" gutterBottom>
        {data?.node?.__typename}: {data?.node?.id}
      </Typography>
      <Typography>
        <strong>Parent tasks: </strong>
        {data?.node?.parents?.edges?.map((e, i, { length }) => {
          return (
            <React.Fragment key={e?.node?.parent?.id}>
              <Link to={`/GeneralTask/${e?.node?.parent?.id}`}>{e?.node?.parent?.id || ""}</Link>
              {i + 1 !== length && <span>, </span>}
            </React.Fragment>
          );
        })}
      </Typography>
      <Container maxWidth="xl">
        <InfoTable
          created={data?.node?.created ? (data?.node?.created as string) : undefined}
          duration={data?.node?.duration}
          state={data?.node?.state}
          result={data?.node?.result}
          task_type={data?.node?.task_type}
          model_type={data?.node?.model_type}
          executor={data?.node?.executor}
        />
        {!!data?.node?.files?.edges?.length && <FileTable data={data?.node?.files?.edges} />}
        {data?.node?.hazard_solution && (
          <HazardTable hazard_solution={data?.node?.hazard_solution} solution_page={false} />
        )}
        {data?.node.srm_logic_tree && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>SRM Logic Tree</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PrettyJson json={data.node.srm_logic_tree} />
            </AccordionDetails>
          </Accordion>
        )}

        {data?.node.gmcm_logic_tree && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>GMCM Logic Tree</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PrettyJson json={data.node.gmcm_logic_tree} />
            </AccordionDetails>
          </Accordion>
        )}

        {data?.node.openquake_config && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>OpenQuake Config</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PrettyJson json={data.node.openquake_config} />
            </AccordionDetails>
          </Accordion>
        )}

        {data?.node?.config && <ConfigTable config={data?.node?.config} solution_page={false} />}
        {data?.node?.arguments && <KeyValueTable header="Arguments" data={data?.node?.arguments} />}
        {data?.node?.environment && <KeyValueTable header="Environment" data={data?.node?.environment} />}
        {data?.node?.metrics && <KeyValueTable header="Metrics" data={data?.node?.metrics} />}
      </Container>
    </PageContainer>
  );
};

export const AutomationTaskPage: React.FC = () => {
  return <React.Suspense fallback={<SimpleBackdrop />}>{<AutomationTask />}</React.Suspense>;
};

export default AutomationTaskPage;

const automationTaskPageQuery = graphql`
  query AutomationTaskPageQuery($id: ID!) {
    node(id: $id) {
      id
      __typename

      ... on AutomationTaskInterface {
        duration
        created
        result
        task_type
        state
        metrics {
          k
          v
        }
        arguments {
          k
          v
        }
        environment {
          k
          v
        }
        general_task_id
        parents {
          edges {
            node {
              parent {
                id
              }
            }
          }
        }
      }

      # AT specific attributes here (NONE right now)
      ... on AutomationTask {
        model_type
        files {
          edges {
            node {
              # id
              role
              file {
                __typename
                ... on Node {
                  id
                }
                ... on FileInterface {
                  file_name
                  file_url
                }
              }
            }
          }
        }
      }

      # RGT specific attributes here (NONE right now)
      ... on RuptureGenerationTask {
        model_type
        files {
          edges {
            node {
              # id
              role
              file {
                __typename
                ... on Node {
                  id
                }
                ... on FileInterface {
                  file_name
                  file_url
                }
              }
            }
          }
        }
      }

      # OHT specific attributes here
      ... on OpenquakeHazardTask {
        model_type
        srm_logic_tree
        gmcm_logic_tree
        openquake_config
        executor
        hazard_solution {
          id
          created
          csv_archive {
            id
            file_name
            file_size
            file_url
          }
          hdf5_archive {
            id
            file_name
            file_size
            file_url
          }
        }
        config {
          # legacy
          id
          created
          source_models {
            ... on Node {
              id
              __typename
            }
            ... on FileInterface {
              file_name
              file_url
              meta {
                k
                v
              }
            }
          }
          template_archive {
            meta {
              k
              v
            }
            file_name
            file_size
            file_url
            md5_digest
          }
        }
      }
    }
  }
`;
